/* Серверный режим экрана «Карточка для маркетплейса» (mcard) на движке шаблона (план вехи 3, lane D; mcard-infog §2B).
   createMcard(createMcardServer) (features/mcard/engine.ts) отдаёт сюда крючки экрана, а каждая вставка SV спрашивает этот объект:
   цена, готовность, фото, создание, хвост и итог берутся только из сервиса заказа кадра (OrderService привязки EngineFrame).
   Правила:
   - одна привязка — один кадр ServerStudio (ключ кадра и его сервис заказа): у другого кадра — и у нового сервиса под прежним ключом —
     своё состояние экрана (S), свой мост загрузок и свой хвост BotRun, поэтому фото, согласие и заказ одного приложения или владельца
     не видны и не уходят под другим;
   - движок не пишет в sessionStorage и не зовёт создание сам: заявку, ключ, отправку, опрос и отказы ведёт сервис (run-driver.ts);
   - S → входы — одна точка записи (inputs-mirror; зеркало зовёт syncFoot движка): те же ключи и кодирование, что у родной формы
     (mcard-inputs.ts, MCardOrderForm.tsx); входы, изменённые не зеркалом (повтор из «Истории», заявка родной формы), — в S (viewOf);
   - фото товара — лоток моста загрузок (media-bridge: слоты формы product_photo, product_photo_2…, согласие первым, одна очередь,
     «Продолжить загрузку» тем же id, окончательный отказ убирает файл); ключи слотов пишет мост, S.files — только его записи;
   - работы «Что сделать» (OPS) и «Сколько фото» (NPH) сужаются на месте до опубликованных; варианты работ — только опубликованные
     значения в порядке формы (SRW 'vars'), подписи — словари экранов (product-model) и данные шаблона; BotText не читается;
   - цена на кнопке — сервиса (studioExpectedPrice: цена сочетания работ); цены в подписях работ — только опубликованные (prices полей),
     без своей арифметики; выбор, цена которого не опубликована (не то «Сколько фото»), закрывает «Создать» (D11, Q-MI5);
     скидка «дешевле» — только если форма её публикует и она посчитана для этой же цены (D11);
   - итог — файлы заказа по секциям сервера (result.sections: photos, slides, video, description) в вкладках шаблона; «Изменить»
     у вкладки — свой инструмент с теми же входами (как родной итог ProductOrderResult); тексты — словарь профиля в момент показа. */
import { endpoints } from '../../api';
import type { SpeechOrder } from '../../api/speech';
import { accountLocale, uiFormat, uiText } from '../../i18n';
import { circleBalance, forgetCircleBalance, rememberCircleBalance } from '../studio/circle/tool-helpers';
import { cNum, tailCurrent, tailSteps } from '../studio/circle/order-tail-model';
import { studioCheapOffer, type StudioApp, type StudioForm } from '../studio/studio-api';
import { record } from '../studio/studio-content';
import { openStudioTarget } from '../studio/studio-navigation';
import { esc, failText, receipt, serverFoot, type BotRunApi, type BotRunRun } from '../studio/engine-kit/bot-run-server';
import { openConsentSheet, type ConsentSheet } from '../studio/engine-kit/consent-sheet';
import { ENGINES, type EngineBinding, type EngineBound, type EngineScreen } from '../studio/engine-kit/engine-host';
import { isImproveKind } from '../studio/engine-kit/improve-service';
import { createInputsMirror, sameInputs, type InputsMirror } from '../studio/engine-kit/inputs-mirror';
import { createMediaBridge, mediaPolicy, type MediaBridge, type MediaTray } from '../studio/engine-kit/media-bridge';
import { uploadFailureText, type Intent, type OrderService } from '../studio/engine-kit/order-service';
import { archive, createResultGallery, PRODUCT_LIMITS, sections, type GalleryItem, type ResultGallery, type ResultSection } from '../studio/engine-kit/result-gallery';
import { createResultMedia, type ResultMedia } from '../studio/engine-kit/result-media';
import { createRunDriver, peekIntent, type RunDriver } from '../studio/engine-kit/run-driver';
import { poorBalance, serverUi, type ServerUi } from '../studio/engine-kit/server-ui';
import { screenDoc } from '../studio/engine-kit/screen-doc';
import { pshootScreen } from '../pshoot/pshoot-model';
import { chatClick, chatFoot, doneP, openSettings } from '../studio/engine-kit/tail-done';
import { DESC_TONES, INFOG_STYLES, MCARD_MOTIONS, MCARD_SETS, UUID, type Choice } from '../slides/product-model';
import { EXTRACT_EMPTY, extractEmpty, extractFailure, extractProduct } from '../slides/product-extract';
import { productDefaults } from '../slides/slide-model';
import { comboOf, inputsOf, mcardFields, soloOf, viewOf, viewOfInputs, workOf, WORKS, worksPublished, type McardView, type McardWork } from './mcard-inputs';
import { MCARD_DEFAULTS, MCARD_INITIAL, mcardContractSupported } from './mcard-model';

/* ---------- контракт с движком (features/mcard/engine.ts, serve({...})) ---------- */

/** Фото лотка в виде шаблона; id — запись моста загрузок */
export interface McardFile {
  name: string;
  size: number;
  url: string;
  w: number;
  h: number;
  id?: string;
  demo?: string;
}
/** Состояние экрана шаблона (S), см. engine.ts §2 */
export interface McardState {
  files: McardFile[];
  pick: Record<McardWork, boolean>;
  text: string;
  desc: string;
  plat: string;
  vars: Record<McardWork, string | number>;
  nph: number;
  own: Record<string, string[]>;
  wish: string;
  cheap: boolean;
  sh: string | null;
  tabReq: string | null;
}
/** Работа «Что сделать» шаблона (OPS) */
export interface McardOp {
  k: McardWork;
  n: string;
  cnt?: () => number;
  u?: string;
  price(): number | null;
  w: string;
  tab: string;
  sh: string;
}
/** Вариант работы шаблона (набор сцен, оформление, движение, стиль текста) */
export interface McardVariant {
  id: string | number;
  n: string;
  sub?: string;
  b?: string;
  d?: string;
  h?: string;
  t?: string;
  g?: string;
  s?: string;
  sc?: string[];
  [key: string]: unknown;
}
/** Настройки страницы (BotCore.uiDefaults и init каркаса): то, что читает SRV */
export interface EngineUi {
  buzz(kind?: string): void;
  tip(el: Element | null, text: string): void;
  [key: string]: unknown;
}
interface EngineUpload {
  o: { mb?: number; multi?: number; [key: string]: unknown };
  say(text: string): void;
  paint(): boolean;
}
interface EngineFoot {
  poor(): boolean;
  poorOpen(): void;
  sync(): void;
}
interface EngineSettings {
  sync(): void;
  close(): void;
  readonly isOpen?: boolean;
}
interface EngineSheet {
  close(): void;
  readonly isOpen?: boolean;
}
/** Крючки, которые движок отдаёт serve (engine.ts, после CAM): tail, up, foot, sc, sh и glyph — геттеры */
export interface McardEngineHooks {
  S: McardState;
  CAM: { st: 'idle' | 'asking' | 'on'; req: unknown; stream: unknown; sim: boolean };
  /** Работы и количества фото шаблона: сужаются на месте до опубликованных */
  OPS: McardOp[];
  NPH: number[];
  readonly tail: BotRunApi;
  readonly up: EngineUpload;
  readonly foot: EngineFoot;
  readonly sc: EngineSettings;
  readonly sh: EngineSheet;
  readonly glyph: Record<string, string>;
  root(): HTMLElement | null;
  mounted(): boolean;
  ui(): EngineUi;
  ic(name: string): string;
  esc(value: string): string;
  lines(text?: string): string[];
  nameOf(): string;
  advOf(): string[];
  catOf(): string;
  varsOf(k: McardWork): McardVariant[];
  varOf(k: McardWork): McardVariant;
  sel(): McardOp[];
  setup(o: unknown): void;
  setLimits(o: { files?: number; mb?: number; tmax?: number; wmax?: number }): void;
  setFiles(list: McardFile[]): void;
  paintPage(): void;
  upPaint(): void;
  opsSync(): void;
  rowSync(k: McardWork, anim?: boolean, keep?: boolean): void;
  ribPaint(k: McardWork): void;
  coversSync(ks?: McardWork[], ms?: number): void;
  descApply(got?: { name: string; adv: string[] }): void;
  camStop(): void;
  vstop(): void;
  boxRepaint(): void;
  tabRepaint(): void;
  sheetClose(): void;
  runSteps(): string[][];
}
type Missing = '' | 'load' | 'off' | 'blocked' | 'unavailable';
type Ready = '' | 'upload' | 'photo';
type Last = '' | 'combo' | 'text' | 'count' | 'price';

export interface McardServer {
  ui<U extends object>(base: U): ServerUi<U>;
  entry(o: unknown): Record<string, unknown>;
  open(o: unknown): void;
  close(): void;
  tail(create: (o: Record<string, unknown>) => BotRunApi, opts: Record<string, unknown>): BotRunApi;
  wrap<T>(slot: 'cheap' | 'plat' | 'vars', value: T, data?: unknown): T;
  /** Текстовый шов ядра (L-TX): строки BotCore словарём профиля */
  tx(template: string, ...values: unknown[]): string;
  /** Опубликовано ли: own — «Свой набор», nph — «Сколько фото», plat — площадка, wish — пожелания */
  has(k: string): boolean;
  missing(): Missing | null;
  ready(): Ready;
  last(): Last;
  missText(kind: string): string;
  label(): string | null;
  price(cheap: boolean): number;
  opPrice(k: McardWork): number | null;
  meta(o: McardOp): string;
  perShot(): string;
  nphUnit(n: number): string;
  improveKind(): string;
  varPic(k: McardWork, v: McardVariant): string | null;
  commit(): void;
  upOpts(): { accept: string; tx(template: string, ...values: unknown[]): string; onPick(files: File[], at?: number): void };
  pick(files: File[]): void;
  remove(i: number): void;
  camText(): string;
  extract(at: HTMLElement | null): void;
  start(btn: HTMLElement): void;
  snap(): Record<string, unknown>;
  shot(sn: Record<string, unknown>, i: number, ar?: string): string;
  slides(sn: Record<string, unknown>): string[];
  seo(sn: Record<string, unknown>): { title: string; desc: string; bullets: string[]; keys: string[] };
  play(btn: HTMLElement): boolean;
  videoStop(): void;
  click(d: DOMStringMap, el: HTMLElement, r: BotRunRun | null): boolean;
  leave(go: () => void): void;
  save(btn: HTMLElement): void;
  change(k: string): void;
  steps(): string[][];
  drive(r: BotRunRun): void;
  failText(r: BotRunRun): string | null;
  receipt(r: BotRunRun): string;
  /** «Назад» внутри экрана: листы закрываются первыми; true — шаг сделан */
  back(): boolean;
}

/* ---------- общие части ---------- */

interface Core {
  html: { unit(n: number, rest?: string): string };
  prefs: { platform: { get(): string } };
}
const core = () => window.BotCore as unknown as Core;
const MB = 1024 * 1024;
/** «Занята» кнопка шаблона (SteosUI.busy): крутилка, пока идёт действие */
function busyButton<T>(btn: HTMLElement | null, fn: () => Promise<T>): Promise<T | undefined> {
  const busy = window.SteosUI?.busy;
  return btn && busy ? busy(btn, fn) : fn();
}
/** accept рамки BotCore (её проверка типа — до моста): точные типы и предел — у моста по политике слота формы */
const ACCEPT = 'image/*,.jpg,.jpeg,.png,.webp,.heic,.heif';
const TRANSPORT = uploadFailureText(null);
const FORM_SAY = [
  'Некоторые файлы повтора больше недоступны. Загрузите их заново.',
  'Этот инструмент сейчас недоступен. Попробуйте обновить форму.',
  'Инструмент обновляется. Обновите форму позже.',
];
/** Подписи вариантов к опубликованным значениям (словари экранов, product-model). Набор «Каталог» — с меткой «Хит», как у шаблона: в
    bot-pshoot.js SETS набор cat каждой категории помечен «Хит» (метки других наборов шаблона зависят от категории товара, которой контракт не публикует) */
const PHOTO_SETS: Record<string, Choice> = { ...MCARD_SETS, ...(MCARD_SETS.cat ? { cat: { ...MCARD_SETS.cat, badge: MCARD_SETS.cat.badge ?? 'Хит' } } : {}) };
const DICT: Record<McardWork, Record<string, Choice>> = { photo: PHOTO_SETS, slides: INFOG_STYLES, video: MCARD_MOTIONS, desc: DESC_TONES };
/** Вид секции итога сервера по работе шаблона */
const SECTION: Record<McardWork, string> = { photo: 'photos', slides: 'slides', video: 'video', desc: 'description' };
/** Имена файлов «Скачать» (те же, что у шаблона и родного итога) */
const SAVE_NAME: Record<McardWork, string> = {
  photo: 'steos-card-photos.zip',
  slides: 'steos-card-slides.zip',
  video: 'steos-card-video.mp4',
  desc: 'steos-card-description.txt',
};
/** Тексты страницы заказа (T шаблона, engine.ts TAIL): failText берёт заголовки отказа и сбоя отсюда */
const FAIL = {
  modH: 'Фото или текст не прошли проверку',
  modWhy: 'Чужие товарные знаки, политику и запрещённое не пропускаем.',
  techH: 'Не получилось собрать карточку',
  notMade: 'карточка не создана',
};
/** Картинка слайда сервера во всю рамку слайда шаблона (.igs) */
const IMG_STYLE = 'position:absolute;inset:0;width:100%;height:100%;object-fit:cover;display:block';
/** Описание итога файлом TXT: четыре блока через пустую строку — заголовок, текст, пункты, ключи (формат «Скачать» шаблона, Q-MI6) */
export function seoOf(text: string | undefined): { title: string; desc: string; bullets: string[]; keys: string[] } {
  const value = String(text ?? '').replace(/\r/g, '').trim();
  const blocks = value.split(/\n\s*\n/).map(block => block.trim()).filter(Boolean);
  if (blocks.length !== 4) return { title: '', desc: value, bullets: [], keys: [] };
  return {
    title: blocks[0],
    desc: blocks[1],
    bullets: blocks[2]
      .split('\n')
      .map(line => line.replace(/^[\s•·*\-–—]+/, '').trim())
      .filter(Boolean),
    keys: blocks[3]
      .split(/,\s*/)
      .map(key => key.trim())
      .filter(Boolean),
  };
}
/** Название и преимущества из поля — как lines() шаблона (первая строка, затем непустые строки без маркеров) */
function parseText(text: string) {
  const lines = String(text)
    .split('\n')
    .map(line => line.replace(/^[\s•·*\-–—]+/, '').trim())
    .filter(Boolean);
  return { name: lines[0] ?? '', adv: lines.slice(1) };
}
/** Показательный товар обложек оформлений без своего текста (DEMO «Слайдов», bot-infog.js): на языке профиля */
const DEMO = { name: 'Беспроводные наушники', adv: ['До 40 часов без подзарядки', 'Активное шумоподавление', 'Мягкие амбушюры из эко-кожи', 'Быстрая зарядка за 10 минут'] };
const price = (value: unknown) => (typeof value === 'number' && Number.isSafeInteger(value) && value >= 0 ? value : null);

/** Состояние одной привязки (кадра ServerStudio) */
interface Frame {
  b: EngineBinding;
  key: string;
  svc: OrderService;
  app: StudioApp;
  bridge: MediaBridge;
  mirror: InputsMirror;
  tray: MediaTray | null;
  form: StudioForm | null;
  layout: string;
  restored: string;
  said: string;
  consent: ConsentSheet | null;
  extract: AbortController | null;
  formShown: boolean;
  balanceAsk: boolean;
  orderSeen: number | undefined;
  settled: () => void;
  first: Promise<void>;
  driver: RunDriver;
  peek: Intent | null;
  /** Файлы итога (один набор на id:revision показанного итога) */
  gallery: ResultGallery;
  /** Ролик итога: настоящий <video> в рамке шаблона .mc-vf .pv */
  media: ResultMedia;
  fresh: boolean;
}

/* ---------- сервер экрана ---------- */

let instance: { bind(b: EngineBinding): EngineBound } | null = null;

export function createMcardServer(h: McardEngineHooks): McardServer {
  const { S } = h;
  let frame: Frame | null = null;
  let bound: Frame | null = null;
  /** Кадр, к которому относятся S и экземпляр хвоста BotRun */
  let painted: Frame | null = null;
  let tailOpts: { create: (o: Record<string, unknown>) => BotRunApi; opts: Record<string, unknown> } | null = null;
  let inst: BotRunApi | null = null;
  const facade = {} as BotRunApi;
  /** Шаблонные значения на новый кадр (engine.ts §1–2): работы, количества, отметки, варианты, число фото */
  const fresh = {
    ops: h.OPS.slice(),
    nph: h.NPH.slice(),
    pick: { ...S.pick },
    vars: { ...S.vars },
    n: S.nph,
  };

  const cur = () => (bound && bound === frame ? frame : null);
  const live = (f: Frame) => {
    const s = f.svc.get();
    return { s, d: f.svc.derive(s) };
  };
  const formOf = () => (cur() ?? frame)?.svc.get().form ?? null;
  const onForm = () => h.mounted() && !h.tail.run;
  /** Подсказка-стекло у элемента; элемента нет на месте — у кнопки подвала */
  const tip = (el: Element | null, text: string) => {
    const at = el?.isConnected ? el : (h.root()?.querySelector('.ap-foot .btn') ?? null);
    if (text && at) h.ui().tip(at, uiText(text));
  };
  const slotKeys = (form: StudioForm | null | undefined) => mcardFields(form).photos.map(field => field.key);
  /** Заявка заказа: сервиса, а пока он не проверил владельца — подсмотренная при открытии */
  const intentOf = (f: Frame | null): Intent | null => {
    const s = f?.svc.get();
    if (!f || !s) return null;
    return s.intent ?? (!s.identity && !s.integrityError ? f.peek : null);
  };
  const shownOrder = (f: Frame | null): SpeechOrder | null => f?.svc.get().order ?? null;
  /** Вид S для входов */
  /** Отметки работ: только работы на экране (OPS сужен до опубликованных) */
  const picked = () => Object.fromEntries(WORKS.map(work => [work, !!S.pick[work] && h.OPS.some(op => op.k === work)])) as Record<McardWork, boolean>;
  const sView = (): McardView => ({ text: S.text, pick: picked(), nph: S.nph, plat: S.plat, wish: S.wish, vars: { ...S.vars } });

  /* ---- фото лотка ---- */
  /** S.files — записи лотка моста, у которых есть что показать (превью повтора, пока читается, не показывается) */
  function filesOf(f: Frame): McardFile[] {
    return (f.tray?.list() ?? [])
      .filter(item => !!item.url)
      .map(item => ({ name: item.name, size: item.size, url: item.url, w: item.meta.width ?? 1200, h: item.meta.height ?? 1200, id: item.id }));
  }
  /** Лоток моста изменился: S.files заново, рамка и всё, что зависит от фото (обложки лент, кнопка) */
  function trayChanged(f: Frame) {
    if (cur() !== f) return;
    h.setFiles(filesOf(f));
    if (onForm()) h.upPaint();
  }
  /** Фото одного выбора приходят в лоток по одному (put моста на каждый файл): рамка перерисовывается один раз на выбор, как у шаблона,
      где выбранные фото встают разом. Иначе trayPaint шаблона видит каждое следующее фото «новым» и докручивает ленту в конец */
  const putting = new WeakSet<Frame>();
  function putSoon(f: Frame) {
    if (putting.has(f)) return;
    putting.add(f);
    queueMicrotask(() => {
      putting.delete(f);
      trayChanged(f);
    });
  }
  const rightsNeeded = (f: Frame) => !f.svc.get().rights && !!f.tray?.list().some(item => item.state === 'ready');
  const CONSENT_TEXTS = { row: 'Права на фото товара', sub: 'Подтверждаю право использовать фото товара' };
  function confirmRights(f: Frame) {
    f.consent?.close();
    f.consent = openConsentSheet({
      rights: () => f.svc.get().rights,
      locked: () => f.svc.derive().locked,
      setRights: on => f.svc.setters.rights(on),
      confirm: true,
      texts: CONSENT_TEXTS,
      onContinue: () => {
        f.consent = null;
        if (cur() === f) h.foot.sync();
      },
      onClose: () => {
        f.consent = null;
      },
    });
  }

  /* ---- готовность ---- */
  function missing(): Missing | null {
    const f = cur();
    if (!f) return 'load';
    const { s, d } = live(f);
    if (s.intent && !d.orderId) return '';
    if (!s.identity && !s.integrityError) return 'load';
    if (s.integrityError || !s.identity || s.identity.appId !== f.app.id) return 'blocked';
    if (!s.form) return s.error ? '' : 'load';
    if (s.versionStale) return '';
    if (!mcardContractSupported(s.form)) return 'off';
    if (!s.form.available) return 'unavailable';
    if (recovery(f)) return '';
    return null;
  }
  function ready(): Ready {
    const f = cur();
    if (!f) return 'upload';
    if (f.bridge.pending()) return 'upload';
    const { s } = live(f);
    /* фото повтора с неверным id загрузки (родная форма: «Проверьте загрузку фото») */
    return slotKeys(s.form).some(key => s.inputs[key] && !UUID.test(s.inputs[key])) ? 'photo' : '';
  }
  /** После правил шаблона: сочетание работ не опубликовано, обязательное поле пусто, число фото не опубликовано, цена выбора не подтверждена */
  function last(): Last {
    const f = cur();
    const form = f?.svc.get().form;
    if (!f || !form) return '';
    const fields = mcardFields(form);
    if (fields.outputs && !comboOf(fields.outputs, picked())) return 'combo';
    if ((fields.text?.required || fields.name?.required) && !S.text.trim()) return 'text';
    if (S.pick.photo && fields.count) {
      const n = String(S.nph);
      if (!fields.count.values.includes(n)) return 'count';
      /* цена сочетания работ (studioExpectedPrice) посчитана для числа фото формы по умолчанию: другое число — цена не подтверждена (D11) */
      const preset = record(fields.count).default_value;
      const base = typeof preset === 'string' ? preset : MCARD_INITIAL.photo_count;
      if (form.chargeable !== false && price(fields.outputs?.prices?.[comboOf(fields.outputs, picked()) ?? '']) !== null && n !== base) return 'price';
    }
    return '';
  }
  function recovery(f: Frame): ((btn: HTMLElement) => void) | null {
    const { s } = live(f);
    if ((!s.form && s.error && !s.integrityError) || s.versionStale) return () => f.svc.setters.revision(value => value + 1);
    if (f.bridge.stalled() && !s.busy && s.error === TRANSPORT) return btn => void busyButton(btn, () => f.bridge.resume());
    if (s.form && rightsNeeded(f)) return () => confirmRights(f);
    return null;
  }
  function label(): string | null {
    const f = cur();
    if (!f) return null;
    const { s, d } = live(f);
    if (s.intent && !d.orderId) return uiText('Продолжить проверку');
    if ((!s.form && s.error && !s.integrityError) || s.versionStale) return uiText('Обновить форму');
    if (f.bridge.stalled() && !s.busy && s.error === TRANSPORT) return uiText('Продолжить загрузку');
    if (s.form && rightsNeeded(f)) return uiText('Подтвердите права на фото');
    if (s.form?.chargeable === false) return uiText(h.sel().length > 1 ? 'Создать всё' : 'Создать');
    return null;
  }
  /** Скидка «дешевле» формы — только для той цены, для которой она посчитана (цена формы, D11) */
  function cheapOffer(f: Frame | null) {
    if (!f) return null;
    const { s, d } = live(f);
    const offer = studioCheapOffer(s.form);
    return offer && s.form && d.price === s.form.expected_price ? offer : null;
  }

  /* ---- сверка формы с сервисом ---- */
  /** Работы и количества шаблона — только опубликованные (порядок шаблона); поля нет — как в шаблоне */
  function narrow(form: StudioForm) {
    const f = mcardFields(form);
    const works = worksPublished(f.outputs);
    const ops = fresh.ops.filter(op => works.includes(op.k));
    h.OPS.splice(0, h.OPS.length, ...(ops.length ? ops : fresh.ops));
    const counts = f.count ? fresh.nph.filter(n => f.count!.values.includes(String(n))) : [];
    h.NPH.splice(0, h.NPH.length, ...(counts.length ? counts : fresh.nph));
  }
  /** Входы → S (повтор, заявка родной формы, значения по умолчанию) */
  function applyView(view: McardView) {
    S.text = view.text;
    if (view.pick) Object.assign(S.pick, view.pick);
    if (view.nph !== undefined && h.NPH.includes(view.nph)) S.nph = view.nph;
    if (view.plat) S.plat = view.plat;
    if (view.wish !== undefined) S.wish = view.wish;
    for (const work of WORKS) if (view.vars[work] !== undefined) S.vars[work] = view.vars[work]!;
  }
  /** Фото повтора: id слотов во входах, которых нет в лотке, — превью (один раз на набор); записи, которых больше нет во входах, уходят */
  function photosSync(f: Frame) {
    const tray = f.tray;
    if (!tray) return;
    const { s, d } = live(f);
    const ids = tray.keys().map(key => s.inputs[key]).filter((id): id is string => !!id);
    const list = tray.list();
    const known = (id: string) => list.some(item => item.uploadId === id || item.pendingId === id);
    if (ids.length && !ids.every(known) && f.restored !== ids.join()) {
      f.restored = ids.join();
      void tray.restore(ids).finally(() => {
        if (cur() === f && h.mounted()) changed(f);
      });
      return;
    }
    if (d.locked || f.bridge.pending()) return;
    for (const item of list) if (item.state === 'ready' && item.uploadId && !ids.includes(item.uploadId)) tray.remove(item.id);
  }
  /** Раскладка формы на экране: опубликованные работы, количества, слоты «Ещё настройки» и значения вариантов */
  const layoutOf = (f: Frame) => {
    const form = f.svc.get().form;
    if (!form) return '';
    const fields = mcardFields(form);
    return JSON.stringify([
      h.OPS.map(op => op.k),
      h.NPH,
      ['nph', 'plat', 'wish'].map(k => server.has(k)),
      WORKS.map(work => fields.variants[work]?.values ?? null),
      fields.platform?.values ?? null,
    ]);
  };
  /** Значения по умолчанию: входы родной формы до загрузки (все работы, 8 фото), затем умолчания выборов — только в опубликованные
      выборы и не поверх заявки */
  function defaultsOf(form: StudioForm, inputs: Record<string, string>): Array<readonly [string, string]> {
    const fields = mcardFields(form);
    const add: Array<readonly [string, string]> = [];
    for (const [field, value] of [
      [fields.outputs, MCARD_INITIAL.outputs],
      [fields.count, MCARD_INITIAL.photo_count],
    ] as const)
      if (field && !inputs[field.key]) {
        /* умолчание формы, затем вход родной формы до загрузки (если он опубликован) */
        const preset = record(field).default_value;
        const choice = typeof preset === 'string' && field.values.includes(preset) ? preset : field.values.includes(value) ? value : undefined;
        if (choice) add.push([field.key, choice]);
      }
    return [...add, ...productDefaults(form, inputs, MCARD_DEFAULTS)];
  }
  function prime(f: Frame): boolean {
    const { s, d } = live(f);
    const form = s.form;
    if ((form || s.error || s.integrityError) && f.settled) f.settled();
    if (form && f.form !== form) {
      f.form = form;
      const fields = mcardFields(form);
      const keys = slotKeys(form);
      const policy = keys[0] ? mediaPolicy(form, keys[0]) : null;
      h.setLimits({
        files: keys.length,
        mb: policy ? policy.maxBytes / MB : 0,
        tmax: fields.text ? (fields.text.max_length ?? 2000) : 600,
        wmax: fields.wishes ? (fields.wishes.max_length ?? 300) : 0,
      });
      narrow(form);
      f.tray = keys.length ? f.bridge.slots(keys) : null;
    }
    if (form && !d.locked && !s.intent) {
      const add = defaultsOf(form, s.inputs);
      if (add.length)
        f.svc.setters.inputs(previous => {
          const next = { ...previous };
          for (const [key, value] of add) if (!next[key]) next[key] = value;
          return next;
        });
    }
    let repaint = false;
    const now = f.svc.get();
    if (form && (f.fresh || f.mirror.external(now.inputs))) {
      if (f.fresh || !sameInputs(inputsOf(form, sView(), now.inputs), now.inputs)) {
        applyView(viewOf(form, now.inputs));
        repaint = true;
      }
      f.fresh = false;
      f.mirror.seen(now.inputs);
    }
    return repaint;
  }
  function paintForm(f: Frame) {
    prime(f);
    f.layout = layoutOf(f);
    h.paintPage();
  }
  function formSync(f: Frame) {
    const repaint = prime(f);
    const now = f.svc.get();
    if (now.form && onForm()) photosSync(f);
    const layout = layoutOf(f);
    if (layout !== f.layout || repaint) {
      f.layout = layout;
      if (onForm()) h.paintPage();
    }
    if (!onForm()) return;
    const error = now.integrityError ? '' : now.error;
    if (error !== f.said) {
      f.said = error;
      if (FORM_SAY.includes(error)) h.up.say(uiText(error));
    }
    h.foot.sync();
  }
  function filesSync(f: Frame) {
    const now = filesOf(f);
    if (now.length !== S.files.length || now.some((file, i) => file.id !== S.files[i]?.id || file.url !== S.files[i]?.url)) trayChanged(f);
  }
  function changed(f: Frame) {
    if (cur() !== f || !h.mounted()) return;
    filesSync(f);
    formSync(f);
    f.bridge.sync();
    f.driver.sync();
    f.consent?.refresh();
    balance(f);
  }
  /** Остаток для «Не хватает Circle» — тот же, что у родной кнопки (GET /app/balance, память на минуту) */
  function balance(f: Frame) {
    const orderId = f.svc.derive().orderId;
    if (orderId && orderId !== f.orderSeen) {
      f.orderSeen = orderId;
      forgetCircleBalance();
    }
    const shown = onForm(),
      first = shown && !f.formShown;
    f.formShown = shown;
    if (!first || f.balanceAsk || circleBalance() !== null || f.svc.get().form?.chargeable === false) return;
    f.balanceAsk = true;
    const seen = f.orderSeen;
    void endpoints
      .balance()
      .then(
        value => {
          if (f.orderSeen === seen) rememberCircleBalance(value?.circle);
        },
        () => {
          /* ошибка — проверки нет, решает сервер (402), как у родной кнопки */
        },
      )
      .finally(() => {
        f.balanceAsk = false;
        if (cur() === f && onForm()) h.foot.sync();
      });
  }

  /* ---- хвост BotRun: фасад одного экземпляра на кадр ---- */
  /** Подпись итога шаблона (doneP): что готово — по вкладкам снимка, словами на языке профиля */
  function doneText(r: BotRunRun | null): string {
    const pick = (r?.snap.pick as Partial<Record<McardWork, boolean>> | undefined) ?? {};
    const words = h.OPS.filter(op => pick[op.k]).map(op => uiText(op.w));
    const what = words.length > 1 ? uiFormat('Готово всё: {0} и {1}.', words.slice(0, -1).join(', '), words[words.length - 1]) : words.length ? uiFormat('Готово {0}.', words[0]) : '';
    return [what, uiText('Бот уже прислал всё в чат, в отдельную тему.')].filter(Boolean).join(' ');
  }
  function rebuild() {
    if (!tailOpts) return;
    const { create, opts } = tailOpts;
    const base = typeof opts.missing === 'function' ? (opts.missing as () => string) : () => '';
    const given = (opts.T as Record<string, unknown>) ?? {};
    const T = Object.fromEntries(Object.entries(given).map(([key, value]) => [key, typeof value === 'string' ? () => uiText(value) : value]));
    const head = (opts.head as Record<string, unknown>) ?? {};
    const foot = { ...((opts.foot as Record<string, unknown>) ?? {}) };
    for (const [key, value] of Object.entries(foot))
      if (Array.isArray(value) && typeof value[2] === 'string') foot[key] = [value[0], value[1], () => uiText(value[2] as string)];
    /* «Скачать» и «Ещё вариант» итога (res шаблона) — подписи словарём профиля: BotRun вставляет их разметкой */
    const res = opts.res as { save?: string; more?: string } | undefined;
    const acts = !opts.acts && res ? () => [res.save ? [res.save, 'download', esc(uiText('Скачать'))] : null, res.more ? [res.more, 'refresh', esc(uiText('Ещё вариант'))] : null].filter(Boolean) : undefined;
    inst = create({
      ...opts,
      ...(acts ? { acts } : {}),
      T: { ...T, doneP: (r: BotRunRun) => doneP(shownOrder(cur() ?? frame), esc(doneText(r ?? h.tail.run)), 'mc') },
      head: { ...head, title: () => uiText(String(head.title ?? '')) },
      drive: (r: BotRunRun) => server.drive(r),
      failText: (r: BotRunRun) => server.failText(r),
      receipt: (r: BotRunRun) => server.receipt(r),
      foot: chatFoot(serverFoot(foot, 'mc'), 'mc'),
      steps: () => server.steps(),
      snap: () => server.snap(),
      price: () => cur()?.svc.derive().price ?? 0,
      missing: () => {
        const f = cur();
        if (f) {
          const { s, d } = live(f);
          if (s.intent || d.orderId) return '';
        }
        return base();
      },
    });
    for (const key of Object.keys(inst)) {
      if (key === 'run' || Object.hasOwn(facade, key)) continue;
      (facade as unknown as Record<string, unknown>)[key] = (...args: unknown[]) =>
        (inst as unknown as Record<string, (...a: unknown[]) => unknown>)[key](...args);
    }
  }
  Object.defineProperty(facade, 'run', { enumerable: true, get: () => inst?.run ?? null });

  /* ---- привязка кадра ---- */
  function bind(b: EngineBinding): EngineBound {
    if (!frame || frame.key !== b.key || frame.svc !== b.svc) {
      if (frame) {
        frame.bridge.cancel();
        frame.consent?.close();
        frame.extract?.abort();
        frame.gallery.release();
        frame.media.release();
      }
      frame = makeFrame(b);
    } else frame.b = b;
    const mine = frame;
    bound = mine;
    return {
      changed: () => changed(mine),
      release() {
        if (bound === mine) bound = null;
      },
      ready: () => mine.first,
      back: () => cur() === mine && server.back(),
    };
  }
  function makeFrame(b: EngineBinding): Frame {
    const f = {} as Frame;
    const guard =
      <A extends unknown[]>(fn: (...args: A) => void) =>
      (...args: A) => {
        if (frame === f) fn(...args);
      };
    let settle!: () => void;
    const first = new Promise<void>(resolve => (settle = resolve));
    Object.assign(f, {
      b,
      key: b.key,
      svc: b.svc,
      app: b.app,
      tray: null,
      form: null,
      layout: '',
      restored: '',
      said: '',
      consent: null,
      extract: null,
      formShown: false,
      balanceAsk: false,
      orderSeen: undefined,
      first,
      settled: () => {
        settle();
        f.settled = undefined as unknown as () => void;
      },
    });
    f.mirror = createInputsMirror(b.svc);
    f.peek = null;
    f.fresh = true;
    f.gallery = createResultGallery(PRODUCT_LIMITS);
    f.media = createResultMedia({ svc: b.svc, ic: name => h.ic(name) });
    f.driver = createRunDriver({
      svc: b.svc,
      tail: () => h.tail,
      root: () => h.root(),
      mounted: () => cur() === f && h.mounted(),
      localSteps: () => server.steps(),
      paintForm: () => (cur() === f ? paintForm(f) : h.paintPage()),
      tip: (el, text) => tip(el, text),
      onDone: () => onDone(f),
    });
    f.bridge = createMediaBridge({
      svc: b.svc,
      owner: () => f.svc.get().identity?.owner,
      say: guard((text: string) => h.up.say(text)),
      put: guard(() => putSoon(f)),
      drop: guard(() => trayChanged(f)),
      update: guard(() => trayChanged(f)),
      consentWith: () => ({ texts: CONSENT_TEXTS }),
    });
    return f;
  }

  /* ---- итог: файлы по секциям сервера ---- */
  /** Секция итога работы k (только известные файлы) */
  const sectionOf = (order: SpeechOrder | null, k: McardWork): ResultSection | undefined => sections(order).find(section => workOf(section.kind) === k || section.kind === SECTION[k]);
  /** Загруженные файлы секции работы k по порядку секции */
  function itemsOf(f: Frame | null, k: McardWork): GalleryItem[] {
    const order = shownOrder(f);
    const section = sectionOf(order, k);
    if (!f || !section) return [];
    const items = f.gallery.items(order);
    return section.indices.map(index => items.find(item => item.index === index)).filter((item): item is GalleryItem => !!item);
  }
  const imgHTML = (url: string | undefined, cls = 'pd-bg', style = '') =>
    url ? `<img class="${cls}" src="${esc(url)}" alt="" draggable="false"${style ? ` style="${style}"` : ''}>` : '';
  /** Кадр в рамке «Фотосессии товара» (.pd-ph): картинка или пустая рамка, пока файл не пришёл */
  const phHTML = (url: string | undefined) => `<span class="pd-ph">${imgHTML(url)}</span>`;
  /* Наборы «Фото в сценах» — те же, что у «Фотосессии товара»: пока фото товара нет, на карточке набора — картинка его первой сцены
     из документа её экрана (категория — электроника, как демо-товар «Слайдов»; model у карточки — mdl у наборов). Без документа — пустая рамка */
  const scenesDoc = screenDoc<Record<string, unknown>>('pshoot', { remember: true, onChange: () => h.coversSync(['photo']) });
  function setPicture(id: string): string | undefined {
    const data = scenesDoc.data();
    if (!data) {
      void scenesDoc.load();
      return undefined;
    }
    const screen = pshootScreen(data);
    const key = id === 'model' ? 'mdl' : id;
    const set = screen.sets.find(item => item.id === key && item.category === 'tech') ?? screen.sets.find(item => item.id === key);
    if (!set) return undefined;
    /* у «С моделью» первая сцена набора — каталожный кадр без человека: на карточке — первая сцена с моделью */
    const scenes = set.scenes.map(id => screen.scenes.find(scene => scene.id === id)).filter(scene => !!scene?.image);
    const withModel = (id: string) => (data.scenes as Array<Record<string, unknown>> | undefined)?.some(raw => raw.id === id && Number(raw.model) > 0);
    return (key === 'mdl' ? scenes.find(scene => scene!.people || withModel(scene!.id)) : undefined)?.image ?? scenes[0]?.image ?? undefined;
  }
  /** Заказ готов (каждая сверка): файлы итога; пришли — вкладки по секциям и картинки на место */
  function onDone(f: Frame) {
    const order = f.svc.get().order;
    if (!order || order.status !== 'done') return;
    void f.gallery.load(order).then(items => {
      if (items && cur() === f) resultPaint(f);
    });
  }
  /** Вкладки итога — по секциям сервера (работы и число фото), затем содержимое открытой вкладки */
  function resultPaint(f: Frame) {
    const r = h.tail.run;
    if (!r || r.st !== 'done' || !h.mounted()) return;
    const order = shownOrder(f);
    const got = sections(order)
      .map(section => workOf(section.kind))
      .filter((work): work is McardWork => !!work);
    if (got.length) {
      const pick = Object.fromEntries(WORKS.map(work => [work, got.includes(work)])) as Record<McardWork, boolean>;
      const photos = sectionOf(order, 'photo')?.indices.length;
      const before = JSON.stringify([r.snap.pick, r.snap.nph]);
      r.snap.pick = pick;
      if (photos) r.snap.nph = photos;
      if (JSON.stringify([r.snap.pick, r.snap.nph]) !== before) {
        h.tail.refresh();
        return;
      }
    }
    /* вкладка «Видео» с идущим роликом не перерисовывается: новая рамка шаблона убрала бы <video> посреди показа
       (файлы галереи догружаются и после «Смотреть») */
    if ((r as { tab?: string }).tab === 'video' && f.media.playing() !== null) return;
    h.tabRepaint();
  }
  /** Поля заявки (входы, что ушли в заказ) или формы */
  const inputsNow = (f: Frame) => (intentOf(f)?.body.inputs ?? f.svc.get().inputs) as Record<string, unknown>;

  const server: McardServer = {
    ui: base =>
      serverUi(base, {
        poorBalance: () => {
          const f = cur();
          if (!f || recovery(f)) return Infinity;
          const s = f.svc.get();
          return poorBalance({ balance: circleBalance(), form: s.form, intent: s.intent }, true);
        },
      }),
    /* вход экрана — входы сервиса (повтор, «Изменить»): в setup шаблона ничего не идёт, S ставит сверка формы */
    entry: () => ({}),
    open(o) {
      const f = cur();
      if (!f) {
        h.setup(o);
        h.paintPage();
        return;
      }
      if (painted !== f) {
        /* другой кадр или новый сервис под прежним ключом: фото, текст, выборы, скидка и хвост прежнего сюда не переходят */
        h.camStop();
        h.sheetClose();
        h.OPS.splice(0, h.OPS.length, ...fresh.ops);
        h.NPH.splice(0, h.NPH.length, ...fresh.nph);
        Object.assign(S, {
          files: [],
          pick: { ...fresh.pick },
          text: '',
          desc: '',
          plat: core().prefs.platform.get(),
          vars: { ...fresh.vars },
          nph: fresh.n,
          own: {},
          wish: '',
          cheap: false,
          sh: null,
          tabReq: null,
        });
        rebuild();
        painted = f;
        f.layout = '';
        f.form = null;
        f.fresh = true;
      }
      f.formShown = false;
      h.setup(o);
      h.setFiles(filesOf(f));
      const root = h.root();
      if (h.tail.run && root) {
        h.tail.show(root);
        changed(f);
        return;
      }
      f.peek = peekIntent(f.app.id);
      if (f.driver.first(f.peek) === 'form' && root) root.scrollTop = 0;
      changed(f);
    },
    close() {
      const f = frame;
      if (!f) return;
      f.consent?.close();
      f.consent = null;
      f.extract?.abort();
      f.extract = null;
      f.media.stop();
    },
    tail(create, opts) {
      tailOpts = { create, opts };
      rebuild();
      return facade;
    },
    wrap<T>(slot: 'cheap' | 'plat' | 'vars', value: T, data?: unknown): T {
      if (slot === 'vars') return variants(data as McardWork, value as unknown as McardVariant[]) as unknown as T;
      if (slot === 'plat') {
        const field = mcardFields(formOf()).platform;
        const list = value as unknown as Array<[string, string]>;
        return (field ? list.filter(([id]) => field.values.includes(id)).map(([id, name]) => [id, uiText(name)]) : list.map(([id, name]) => [id, uiText(name)])) as unknown as T;
      }
      const cheap = value as unknown as { where?: unknown; go?: unknown };
      return {
        where: () => uiText(String(cheap.where ?? '')),
        get price() {
          return cheapOffer(cur()) ? () => server.price(true) : undefined;
        },
        get go() {
          return cheapOffer(cur()) ? cheap.go : undefined;
        },
        get percent() {
          return cheapOffer(cur())?.percent;
        },
      } as unknown as T;
    },
    tx: (template, ...values) => uiFormat(template, ...values),
    has(k) {
      const fields = mcardFields(formOf());
      if (k === 'nph') return !!fields.count;
      if (k === 'plat') return !!fields.platform;
      if (k === 'wish') return !!fields.wishes;
      /* «Свой набор» (сцены на выбор): форма не публикует сцен набора — его нет (Q-MI7) */
      return false;
    },
    missing,
    ready,
    last,
    missText(kind) {
      const f = cur();
      const s = f?.svc.get();
      switch (kind) {
        case 'load':
          return uiText('Проверяем доступность');
        case 'blocked':
          return uiText(s?.integrityError || 'Проверяем доступность');
        case 'off':
          return uiText('Этот вариант карточки пока недоступен');
        case 'unavailable':
          return uiText('Создание сейчас недоступно');
        case 'upload':
          return uiText('Загружаем фото');
        case 'cam':
          return uiText('Сделайте снимок или закройте камеру');
        case 'file':
          return uiText('Загрузите фото товара');
        case 'pick':
          return uiText('Отметьте, что сделать');
        case 'name':
          return uiText('Напишите название товара первой строкой');
        case 'adv':
          return uiText('Слайдам нужно 3 преимущества, каждое с новой строки');
        case 'photo':
          return uiText('Проверьте загрузку фото');
        case 'combo':
          return uiText('Выберите, что сделать');
        case 'text':
          return uiText('Добавьте название и преимущества');
        case 'count':
          return uiText('Выберите количество фото');
        case 'price':
          return uiText('Цена этого выбора пока не подтверждена');
        case 'long':
          return uiFormat('Текст длиннее {0} знаков', mcardFields(s?.form).text?.max_length ?? 600);
        case 'wish':
          return uiFormat('Пожелания длиннее {0} знаков', mcardFields(s?.form).wishes?.max_length ?? 300);
        default:
          return '';
      }
    },
    label,
    price(cheap) {
      const f = cur();
      if (!f) return 0;
      const offer = cheap ? cheapOffer(f) : null;
      return offer ? offer.price : f.svc.derive().price;
    },
    opPrice(k) {
      const form = formOf();
      if (!form || form.chargeable === false) return null;
      const fields = mcardFields(form);
      if (k === 'photo' && fields.count) {
        const n = price(fields.count.prices?.[String(S.nph)]);
        if (n !== null) return n;
      }
      const solo = soloOf(fields.outputs, k);
      return solo ? price(fields.outputs?.prices?.[solo]) : null;
    },
    meta(o) {
      const cnt = o.cnt ? `<span class="mono">${o.cnt()}</span> ${h.esc(uiText(o.u ?? ''))}` : '';
      const value = o.price();
      return [h.esc(h.varOf(o.k).n), cnt, value === null ? '' : `<span class="mono">${cNum(value)}</span> C`].filter(Boolean).join(' · ');
    },
    perShot() {
      /* бесплатная форма (dev, chargeable: false) цен по числу кадров не шлёт — «по 0 C за кадр», а не прочерк */
      if (formOf()?.chargeable === false) return cNum(0);
      const field = mcardFields(formOf()).count;
      const per = (field?.values ?? []).map(value => {
        const amount = price(field?.prices?.[value]);
        return amount === null || !Number(value) ? null : amount / Number(value);
      });
      return per.length && per.every(value => value !== null && value === per[0] && Number.isSafeInteger(value)) ? cNum(per[0]!) : '—';
    },
    nphUnit(n) {
      const amount = price(mcardFields(formOf()).count?.prices?.[String(n)]);
      return amount === null ? '' : core().html.unit(amount);
    },
    improveKind() {
      const form = formOf();
      if (!form || !mcardFields(form).wishes) return '';
      const kind = record(record(form.constraints).mcard).improvement_kind;
      return isImproveKind(kind) ? kind : '';
    },
    varPic(k, v) {
      const main = S.files[0];
      /* у набора нет картинки (в данных экрана image_url пуст): пустая светлая рамка читалась как незагруженная — значок картинки на тёмной плитке, как у ленты образов */
      const noPicture = '<span class="bcs-no"><svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-linecap="round" stroke-linejoin="round" aria-hidden="true"><rect x="3" y="4" width="18" height="16" rx="3"/><circle cx="9" cy="10" r="1.6"/><path d="m21 16-5-5-8 8"/></svg></span>';
      if (k === 'photo') {
        const url = main?.url ?? setPicture(String(v.id));
        return url ? `<span class="pd-mos n1"><span>${phHTML(url)}</span></span>` : noPicture;
      }
      if (k === 'video') return (main?.url ? phHTML(main.url) : noPicture) + `<span class="mc-mo">${h.glyph[String(v.g ?? '')] ?? ''}</span>`;
      if (k === 'slides') {
        const G = (window as unknown as { BotInfog?: { cover?(id: string, o: Record<string, unknown>): string } }).BotInfog;
        return G?.cover ? G.cover(String(v.id), { name: h.nameOf() || uiText(DEMO.name), adv: h.advOf().length ? h.advOf() : DEMO.adv.map(line => uiText(line)), ...(main ? { img: { src: main.url, own: true } } : {}) }) : '';
      }
      /* стиль текста — лист с заголовком и фразой шаблона (данные варианта, на языке профиля) */
      return null;
    },
    commit() {
      const f = cur();
      const form = f?.svc.get().form;
      if (!f || !form) return;
      f.mirror.commit(previous => inputsOf(form, sView(), previous));
    },
    upOpts: () => ({
      accept: ACCEPT,
      tx: (template: string, ...values: unknown[]) => uiFormat(template, ...values),
      onPick(files, at) {
        const f = cur();
        if (!f || !files.length) return;
        if (!f.tray) {
          h.up.say(uiText('Проверяем доступность'));
          return;
        }
        void f.tray.pickSlots(files, at ?? undefined);
      },
    }),
    pick(files) {
      server.upOpts().onPick(files);
    },
    remove(i) {
      const f = cur();
      const id = S.files[i]?.id;
      if (!f?.tray || !id) return;
      f.tray.remove(id);
      trayChanged(f);
    },
    camText: () => uiText('Камера недоступна. Загрузите файл.'),
    extract(at) {
      const f = cur();
      if (!f || f.extract) return;
      const btn = at?.querySelector<HTMLElement>('.btn') ?? at;
      const abort = new AbortController();
      f.extract = abort;
      void busyButton(btn, () => extractProduct(S.desc, 'mcard', abort.signal))
        .then(found => {
          if (abort.signal.aborted || cur() !== f || !found) return;
          if (extractEmpty(found)) {
            tip(btn, EXTRACT_EMPTY);
            return;
          }
          if (S.sh !== 'desc') return;
          /* как родной лист: пустое название или преимущества сервера не стирают своих */
          h.descApply({ name: found.name || h.nameOf(), adv: found.advantages.length ? found.advantages : h.advOf() });
        })
        .catch(failure => {
          if (!abort.signal.aborted && cur() === f) tip(btn, extractFailure(failure));
        })
        .finally(() => {
          if (f.extract === abort) f.extract = null;
        });
    },
    start(btn) {
      const f = cur();
      if (!f) return;
      const { s, d } = live(f);
      if (s.intent && !d.orderId) {
        h.tail.start();
        return;
      }
      const fix = recovery(f);
      if (fix) {
        fix(btn);
        return;
      }
      if (h.foot.poor()) {
        h.foot.poorOpen();
        return;
      }
      h.tail.start();
    },
    /* снимок входа заказа (r.snap шаблона): из тела заявки (подсмотренной), без неё — из S; фото — только если в лотке то же фото заказа */
    snap() {
      const f = cur();
      const s = f?.svc.get();
      const it = intentOf(f);
      const view = it ? viewOfInputs(s?.form, it.body.inputs as Record<string, unknown>) : sView();
      const pick = view.pick && WORKS.some(work => view.pick![work]) ? { ...view.pick } : { ...S.pick };
      const vars = { ...S.vars, ...view.vars };
      const { name, adv } = parseText(view.text);
      const vn: Record<string, string> = {};
      for (const work of WORKS) {
        const id = String(vars[work] ?? '');
        vn[work] = h.varsOf(work).find(item => String(item.id) === id)?.n ?? uiText(DICT[work][id]?.name ?? id);
      }
      const main = f?.tray?.list()[0];
      const photoKey = slotKeys(s?.form)[0] ?? 'product_photo';
      const own = !!main?.url && (!it || (!!main.uploadId && it.body.inputs[photoKey] === main.uploadId));
      const wish = (view.wish ?? S.wish).trim();
      return {
        files: own ? [{ name: main!.name, size: main!.size, url: main!.url, w: main!.meta.width ?? 1200, h: main!.meta.height ?? 1200 }] : [],
        pick,
        name,
        adv,
        plat: view.plat ?? S.plat,
        cat: h.catOf(),
        vars,
        vn,
        nph: view.nph ?? S.nph,
        sc: [],
        sty: vars.slides,
        mo: vars.video,
        tone: vars.desc,
        wish,
        text: (view.text + '\n' + wish).trim(),
        cheap: !!(it ? it.body.cheap : S.cheap),
        tab: null,
      };
    },
    shot(_sn, i) {
      const f = cur() ?? frame;
      const r = h.tail.run as (BotRunRun & { tab?: string }) | null;
      /* кадр 1 вкладки «Видео» — обложка ролика (картинка секции видео), иначе — кадр i секции фото */
      if (r?.tab === 'video' && i === 1) return phHTML(itemsOf(f, 'video').find(item => item.mime.startsWith('image/'))?.url);
      return phHTML(itemsOf(f, 'photo')[i]?.url);
    },
    slides(sn) {
      const f = cur() ?? frame;
      const items = itemsOf(f, 'slides');
      const n = sectionOf(shownOrder(f), 'slides')?.indices.length ?? 3;
      const cls = `igs ig-${esc(String(sn.sty ?? 'min'))}`;
      return Array.from({ length: n }, (_, i) => `<div class="${cls}">${imgHTML(items[i]?.url, '', IMG_STYLE)}</div>`);
    },
    seo() {
      const item = itemsOf(cur() ?? frame, 'desc').find(entry => entry.text !== undefined);
      return seoOf(item?.text);
    },
    play(btn) {
      const f = cur();
      const order = shownOrder(f);
      const section = sectionOf(order, 'video');
      const asset = order?.result?.assets.find(entry => section?.indices.includes(entry.index) && entry.mime.startsWith('video/'));
      if (!f || !asset) {
        tip(btn, 'Видео ещё загружается');
        return true;
      }
      const ms = record(asset).duration_ms;
      const dur = typeof ms === 'number' ? ms / 1000 : null;
      void f.media.toggle(asset.index, { el: btn, kind: 'frame', dur });
      return true;
    },
    videoStop() {
      (cur() ?? frame)?.media.stop();
    },
    click(d, el) {
      const f = cur();
      if (d.mcRetry !== undefined || d.mcRchk !== undefined || d.mcFix !== undefined) {
        if (!f || f.driver.busy()) return true;
        if (d.mcRetry !== undefined) h.tail.retry();
        else if (d.mcRchk !== undefined) void f.driver.check(el);
        else void f.driver.leave(() => h.tail.exit(() => h.paintPage()), false, '');
        return true;
      }
      if (d.mcChat !== undefined) {
        chatClick({ order: f?.svc.get().order, el, text: 'Тут откроется тема чата, куда бот прислал всё', tip: (at, text) => tip(at, text) });
        return true;
      }
      if (d.mcSettings !== undefined) {
        openSettings();
        return true;
      }
      return false;
    },
    leave(go) {
      const f = cur();
      if (!f) return;
      void f.driver.leave(go, false, '');
    },
    save(btn) {
      const f = cur();
      const order = shownOrder(f);
      const r = h.tail.run as (BotRunRun & { tab?: string }) | null;
      const k = (r?.tab ?? '') as McardWork;
      if (!f || order?.status !== 'done' || !WORKS.includes(k)) return;
      const section = sectionOf(order, k);
      const items = itemsOf(f, k);
      const one = k === 'video' ? (items.find(item => item.mime.startsWith('video/')) ?? items[0]) : items[0];
      const saveBtn = (window.BotCore as unknown as { saveBtn?(btn: HTMLElement, make: () => Promise<{ data: Blob; name: string }>, o: object): void }).saveBtn;
      if (!section || !saveBtn || (!one && !(section.indices.length > 1 && section.downloadUrl))) {
        tip(btn, 'Файлы ещё загружаются');
        return;
      }
      const ui = h.ui();
      /* несколько файлов — архив секции (owner-protected путь заказа), один — сам файл (как родной итог) */
      saveBtn(
        btn,
        async () =>
          section.indices.length > 1 && section.downloadUrl
            ? { data: await archive(order, section.downloadUrl), name: SAVE_NAME[k] }
            : { data: one!.blob, name: one!.name || SAVE_NAME[k] },
        { buzz: () => ui.buzz(), tip: (el: Element, text: string) => ui.tip(el, text) },
      );
    },
    /* «Изменить» у вкладки — свой инструмент с теми же входами, «назад» вернёт на этот итог (как родной итог ProductOrderResult) */
    change(k) {
      const f = cur();
      const r = h.tail.run;
      if (!f || r?.st !== 'done') return;
      h.vstop();
      const inputs = inputsNow(f);
      const fields = mcardFields(f.svc.get().form);
      const value = (key: string | undefined) => (key && typeof inputs[key] === 'string' ? (inputs[key] as string) : undefined);
      const photo = value(fields.photos[0]?.key ?? 'product_photo');
      const platform = value(fields.platform?.key ?? 'platform');
      const text = value(fields.text?.key ?? fields.name?.key ?? 'text');
      const count = value(fields.count?.key ?? 'photo_count');
      const common = { ...(photo ? { product_photo: photo } : {}), ...(platform ? { platform } : {}) };
      const target =
        k === 'photo'
          ? { screen: 'pshoot', options: { ...common, ...(count ? { count } : {}) } }
          : k === 'slides'
            ? { screen: 'infog', options: { ...common, ...(text ? { text } : {}) } }
            : k === 'video'
              ? { screen: 'video', preset: 'cover', options: common }
              : { screen: 'text', tool: 'seo', options: { ...common, ...(text ? { text } : {}) } };
      h.ui().buzz();
      openStudioTarget({ ...target, ret: true });
    },
    steps: () => h.runSteps().map(([key, icon, run, done]) => [key, icon, uiText(run), uiText(done)]),
    drive(r) {
      const f = cur();
      if (f) void f.driver.drive(r);
    },
    failText(r) {
      const f = cur() ?? frame;
      const s = f?.svc.get();
      const order = s?.order ?? null;
      const steps = tailSteps(order);
      return failText(r, {
        order,
        error: s ? s.integrityError || s.error : '',
        texts: FAIL,
        step: steps.length ? steps[tailCurrent(order, steps)] : undefined,
      });
    },
    receipt(r) {
      const f = cur() ?? frame;
      const s = f?.svc.get();
      const cheap = s?.intent ? !!s.intent.body.cheap : !!r.snap.cheap;
      return receipt(r, s?.order ?? null, { cheap, where: 'на фото и видео' });
    },
    back() {
      const f = cur();
      if (f?.consent?.isOpen()) {
        f.consent.close();
        return true;
      }
      if (S.sh) {
        h.sheetClose();
        return true;
      }
      if (h.sc.isOpen) {
        h.sc.close();
        return true;
      }
      return false;
    },
  };

  /** Варианты работы k (SRW 'vars'): опубликованные значения в порядке формы; данные шаблона (картинка, знак, сцены, фразы) — у известных
      id, подписи — словарь экранов; на языке профиля. Поля нет — список шаблона на языке профиля */
  let cache: { key: string; list: McardVariant[] } | null = null;
  function variants(k: McardWork, list: McardVariant[]): McardVariant[] {
    const form = formOf();
    const field = mcardFields(form).variants[k];
    const key = JSON.stringify([k, accountLocale(), field?.values ?? null, list.map(item => [item.id, item.n, item.sc ?? null])]);
    if (cache?.key === key) return cache.list;
    const said = (item: McardVariant): McardVariant => ({
      ...item,
      n: uiText(item.n),
      ...(item.sub ? { sub: uiText(item.sub) } : {}),
      ...(item.d ? { d: uiText(item.d) } : {}),
      ...(item.h ? { h: uiText(item.h) } : {}),
      ...(item.t ? { t: uiText(item.t) } : {}),
      b: uiText(item.b ?? ''),
      ...(item.sc ? { sc: item.sc.slice() } : {}),
    });
    const out = field
      ? field.values
          .filter(value => !(k === 'photo' && value === 'own'))
          .map(value => {
            const known = list.find(item => String(item.id) === value);
            if (known) return said(known);
            const d = DICT[k][value];
            return said({ id: k === 'video' && /^\d+$/.test(value) ? Number(value) : value, n: d?.name ?? value, ...(d?.sub ? { sub: d.sub } : {}), b: d?.badge ?? '', ...(k === 'photo' ? { sc: [] } : {}) });
          })
      : list.map(said);
    cache = { key, list: out };
    return out;
  }

  /* ---- смена языка профиля (i18n: событие steos:locale): открытый экран рисуется заново ---- */
  function relabel(r: BotRunRun) {
    const next = server.steps();
    for (const step of r.steps) {
      const same = next.find(item => item[0] === step[0]);
      if (same) {
        step[2] = same[2];
        step[3] = same[3];
      }
    }
  }
  document.addEventListener('steos:locale', () => {
    const f = cur();
    const r = h.tail.run;
    if (r) relabel(r);
    if (!f || !h.mounted()) return;
    const root = h.root();
    if (r && root) h.tail.show(root);
    else h.paintPage();
    changed(f);
  });
  document.addEventListener('steos:account-updated', () => {
    if (cur() && onForm()) h.foot.sync();
  });

  instance = { bind };
  return server;
}

/** ENGINES.mcard.bind: привязка кадра к серверу экрана, созданному движком (без движка в серверном режиме — пустая привязка) */
export function bindMcardEngine(b: EngineBinding): EngineBound {
  return instance ? instance.bind(b) : { changed() {}, release() {} };
}

/** Экран «mcard» реестра движков студии: движок шаблона window.BotMcard, атрибуты data-mc-*, без правой панели, знак заказа «mcard» */
export const MCARD_ENGINE: EngineScreen = { global: 'BotMcard', prefix: 'mc', side: false, kind: 'mcard', bind: bindMcardEngine };

export function registerMcardEngine() {
  ENGINES.mcard = MCARD_ENGINE;
}
